import React from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Award, 
  Users, 
  Globe, 
  Crown,
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  Instagram,
  Facebook,
  Twitter
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { settingsAPI } from '../services/settingsAPI';
import InlineEditor from '../components/ui/InlineEditor';
import InlineTextArea from '../components/ui/InlineTextArea';


const AboutPage = () => {
  const { getLocalizedSetting, getSetting, siteSettings, setSiteSettings } = useSiteSettings();
  const { language } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const currentYear = new Date().getFullYear();

  // Listen for setting updates from inline editors
  React.useEffect(() => {
    const handleSettingUpdate = (event) => {
      const { key, value } = event.detail;
      setSiteSettings(prev => ({
        ...prev,
        [key]: value
      }));
    };

    window.addEventListener('settingUpdated', handleSettingUpdate);

    // Cleanup
    return () => {
      window.removeEventListener('settingUpdated', handleSettingUpdate);
    };
  }, [setSiteSettings]);

  const defaultHero = 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=1200&h=600&fit=crop&crop=center';
  const heroImage = getSetting('about_hero_image_url', defaultHero);

  // Right image in Our Story section
  const defaultStory = 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=600&h=600&fit=crop&crop=center';
  const storyImage = getSetting('about_story_image_url', defaultStory);

  const fileInputRef = React.useRef(null);
  const fileInputId = 'aboutHeroFileInput';
  const [uploading, setUploading] = React.useState(false);

  const storyFileInputRef = React.useRef(null);
  const [storyUploading, setStoryUploading] = React.useState(false);

  const triggerFilePick = () => {
    if (user?.role !== 'admin') return;
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('photo', file);
      const res = await fetch('/api/demo-photos/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Upload échoué');
      }
      const data = await res.json();
      const url = data?.file?.url;
      if (!url) throw new Error('URL du fichier non reçue');

      await settingsAPI.update('about_hero_image_url', url, token);

      window.dispatchEvent(new CustomEvent('settingUpdated', { detail: { key: 'about_hero_image_url', value: url } }));
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerStoryPick = () => {
    if (user?.role !== 'admin') return;
    if (storyFileInputRef.current) storyFileInputRef.current.click();
  };

  const handleStoryFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setStoryUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('photo', file);
      const res = await fetch('/api/demo-photos/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Upload échoué');
      }
      const data = await res.json();
      const url = data?.file?.url;
      if (!url) throw new Error('URL du fichier non reçue');

      await settingsAPI.update('about_story_image_url', url, token);

      window.dispatchEvent(new CustomEvent('settingUpdated', { detail: { key: 'about_story_image_url', value: url } }));
    } catch (err) {
      console.error(err);
    } finally {
      setStoryUploading(false);
      if (storyFileInputRef.current) storyFileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/20 to-purple-600/20 pointer-events-none"></div>
        <div className="absolute inset-0 bg-cover bg-center opacity-20 pointer-events-none" style={{ backgroundImage: `url(${heroImage})` }}></div>
        {user?.role === 'admin' && (
          <div className="absolute right-6 top-6 z-[60] pointer-events-auto">
            <button onClick={triggerFilePick} className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm">
              {uploading ? 'Téléversement…' : 'Changer la photo'}
            </button>
            <input id={fileInputId} ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
          </div>
        )}
        
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6">
              <InlineEditor settingKey="about_page_title_fr" defaultValue="À Propos de TOLOU">
                {getLocalizedSetting('about_page_title', 'À Propos de TOLOU')}
              </InlineEditor>
          </h1>
            <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              <InlineTextArea settingKey="about_page_subtitle_fr" defaultValue="Notre histoire et notre passion">
                {getLocalizedSetting('about_page_subtitle', 'Notre histoire et notre passion')}
              </InlineTextArea>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-8">
                <InlineEditor settingKey="homepage_our_story_title_fr" defaultValue="Notre Histoire">
                  {getLocalizedSetting('homepage_our_story_title', 'Notre Histoire')}
                </InlineEditor>
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                <InlineTextArea settingKey="homepage_our_story_description_fr" defaultValue="Mahja Bijoux combine l\'élégance française avec la richesse culturelle iranienne pour créer des pièces uniques qui racontent une histoire.">
                  {getLocalizedSetting('homepage_our_story_description', 'Mahja Bijoux combine l\'élégance française avec la richesse culturelle iranienne pour créer des pièces uniques qui racontent une histoire.')}
                </InlineTextArea>
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-300 text-lg">
                    <InlineEditor settingKey="homepage_our_story_feature1_fr" defaultValue="Matériaux de qualité exceptionnelle">
                      {getLocalizedSetting('homepage_our_story_feature1', 'Matériaux de qualité exceptionnelle')}
                    </InlineEditor>
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-300 text-lg">
                    <InlineEditor settingKey="homepage_our_story_feature2_fr" defaultValue="Artisanat traditionnel iranien">
                      {getLocalizedSetting('homepage_our_story_feature2', 'Artisanat traditionnel iranien')}
                    </InlineEditor>
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-gray-300 text-lg">
                    <InlineEditor settingKey="homepage_our_story_feature3_fr" defaultValue="Design moderne et élégant">
                      {getLocalizedSetting('homepage_our_story_feature3', 'Design moderne et élégant')}
                    </InlineEditor>
                  </span>
                </div>
              </div>

              <Link
                to="/products"
                className="inline-flex items-center space-x-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-8 py-4 rounded-lg transition-colors duration-300 text-lg"
              >
                <span>
                  <InlineEditor settingKey="about_discover_button_fr" defaultValue="Découvrir nos collections">
                    {getLocalizedSetting('about_discover_button', 'Découvrir nos collections')}
                  </InlineEditor>
                </span>
                <ArrowRight size={20} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative">
                <img
                  src={storyImage}
                  alt="Persian Jewelry"
                  className="w-full h-96 object-cover rounded-2xl shadow-2xl"
                />
                {user?.role === 'admin' && (
                  <div className="absolute -bottom-6 -right-6 bg-yellow-500 text-gray-900 px-6 py-3 rounded-xl font-bold text-lg cursor-pointer" onClick={triggerStoryPick}>
                    {storyUploading ? 'Téléversement…' : 'Changer la photo'}
                  </div>
                )}
                <input ref={storyFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleStoryFileChange} />
              </div>
              {null}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 xl:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              <InlineEditor settingKey="about_values_title_fr" defaultValue="Nos Valeurs">
                {getLocalizedSetting('about_values_title', 'Nos Valeurs')}
              </InlineEditor>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              <InlineTextArea settingKey="about_values_subtitle_fr" defaultValue="L\'excellence, la tradition et l\'innovation au service de la beauté">
                {getLocalizedSetting('about_values_subtitle', 'L\'excellence, la tradition et l\'innovation au service de la beauté')}
              </InlineTextArea>
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-gray-800 rounded-2xl p-8 text-center hover:bg-gray-700 transition-colors duration-300"
            >
              <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Heart className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                <InlineEditor settingKey="about_value1_title_fr" defaultValue="Passion">
                  {getLocalizedSetting('about_value1_title', 'Passion')}
                </InlineEditor>
              </h3>
              <p className="text-gray-300 leading-relaxed">
                <InlineTextArea settingKey="about_value1_description_fr" defaultValue="Notre passion pour les bijoux nous pousse à créer des pièces exceptionnelles qui émerveillent et inspirent.">
                  {getLocalizedSetting('about_value1_description', 'Notre passion pour les bijoux nous pousse à créer des pièces exceptionnelles qui émerveillent et inspirent.')}
                </InlineTextArea>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gray-800 rounded-2xl p-8 text-center hover:bg-gray-700 transition-colors duration-300"
            >
              <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Award className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                <InlineEditor settingKey="about_value2_title_fr" defaultValue="Excellence">
                  {getLocalizedSetting('about_value2_title', 'Excellence')}
                </InlineEditor>
              </h3>
              <p className="text-gray-300 leading-relaxed">
                <InlineTextArea settingKey="about_value2_description_fr" defaultValue="Nous visons l\'excellence dans chaque détail, de la sélection des matériaux à la finition finale.">
                  {getLocalizedSetting('about_value2_description', 'Nous visons l\'excellence dans chaque détail, de la sélection des matériaux à la finition finale.')}
                </InlineTextArea>
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-gray-800 rounded-2xl p-8 text-center hover:bg-gray-700 transition-colors duration-300"
            >
              <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Globe className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                <InlineEditor settingKey="about_value3_title_fr" defaultValue="Tradition">
                  {getLocalizedSetting('about_value3_title', 'Tradition')}
                </InlineEditor>
              </h3>
              <p className="text-gray-300 leading-relaxed">
                <InlineTextArea settingKey="about_value3_description_fr" defaultValue="Nous préservons et honorons les traditions ancestrales tout en les adaptant au monde moderne.">
                  {getLocalizedSetting('about_value3_description', 'Nous préservons et honorons les traditions ancestrales tout en les adaptant au monde moderne.')}
                </InlineTextArea>
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default AboutPage; 